import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Clock, PlayCircle, StopCircle, TrendingUp, DollarSign, AlertCircle, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/currency";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Shift = {
  id: number;
  staffName: string | null;
  openedAt: string;
  closedAt: string | null;
  openingFloat: number;
  closingCash: number | null;
  expectedCash: number | null;
  difference: number | null;
  totalSalesAmount: number;
  totalSalesCount: number;
  notes: string | null;
  status: "open" | "closed";
};

export default function Shifts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [openingFloat, setOpeningFloat] = useState("");
  const [openNotes, setOpenNotes] = useState("");
  const [closingCash, setClosingCash] = useState("");
  const [closeNotes, setCloseNotes] = useState("");

  const { data: currentShift, isLoading: loadingCurrent, isFetching: refreshingCurrent, error: currentError, refetch: refreshCurrent } = useQuery<Shift | null>({
    queryKey: ["shifts", "current"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/shifts/current`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).error || "Could not load current shift");
      return res.json();
    },
    refetchInterval: 30000,
  });

  const { data: shifts, isLoading: loadingShifts, error: shiftsError } = useQuery<Shift[]>({
    queryKey: ["shifts"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/shifts`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).error || "Could not load shift history");
      return res.json();
    },
  });

  const openMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/shifts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ openingFloat: Number(openingFloat) || 0, notes: openNotes || undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to open shift");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      setShowOpenDialog(false);
      setOpeningFloat("");
      setOpenNotes("");
    },
    onError: (e: Error) => toast({ title: e.message || "Failed to open shift", variant: "destructive" }),
  });

  const closeMutation = useMutation({
    mutationFn: async () => {
      if (!currentShift) return;
      const res = await fetch(`${BASE}/api/shifts/${currentShift.id}/close`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ closingCash: Number(closingCash) || 0, notes: closeNotes || undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to close shift");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      setShowCloseDialog(false);
      setClosingCash("");
      setCloseNotes("");
    },
    onError: (e: Error) => toast({ title: e.message || "Failed to close shift", variant: "destructive" }),
  });

  const isAdmin = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Shift Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Track register sessions, opening floats, and cash reconciliation</p>
        </div>
        {currentError ? null : currentShift ? (
          <Button data-testid="button-close-shift" onClick={() => { setClosingCash(""); setShowCloseDialog(true); void refreshCurrent(); }} variant="destructive" className="gap-2">
            <StopCircle className="w-4 h-4" /> Close Shift
          </Button>
        ) : (
          <Button onClick={() => setShowOpenDialog(true)} className="gap-2">
            <PlayCircle className="w-4 h-4" /> Open Shift
          </Button>
        )}
      </div>

      {/* Current Shift Banner */}
      {loadingCurrent ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm p-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading shift status…
        </div>
      ) : currentError ? (
        <Card className="border-destructive/40">
          <CardContent className="py-5 text-sm text-destructive" data-testid="status-shift-error">
            {(currentError as Error).message}. Cash reconciliation is unavailable until this is resolved.
          </CardContent>
        </Card>
      ) : currentShift ? (
        <Card className="border-emerald-200 bg-emerald-50/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Shift In Progress
              </CardTitle>
              <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50">Open</Badge>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Opened by" value={currentShift.staffName ?? user?.name ?? "—"} icon={<Clock className="w-4 h-4" />} />
            <Stat label="Started at" value={format(new Date(currentShift.openedAt), "h:mm a, MMM d")} icon={<PlayCircle className="w-4 h-4" />} />
            <Stat label="Opening float" value={formatCurrency(currentShift.openingFloat)} icon={<DollarSign className="w-4 h-4" />} />
            <Stat label="Sales this shift" value={`${currentShift.totalSalesCount} sales`} icon={<TrendingUp className="w-4 h-4" />} />
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
              <Clock className="w-6 h-6 text-muted-foreground/50" />
            </div>
            <div>
              <p className="font-semibold">No shift is currently open</p>
              <p className="text-sm text-muted-foreground mt-0.5">Open a shift to start tracking sales for this register session</p>
            </div>
            <Button onClick={() => setShowOpenDialog(true)} className="gap-2 mt-1">
              <PlayCircle className="w-4 h-4" /> Open Shift
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Shift History */}
      <div>
        <h2 className="text-base font-semibold mb-3">Shift History</h2>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="text-right">Float</TableHead>
                <TableHead className="text-right">Cash sales</TableHead>
                <TableHead className="text-right">Closing</TableHead>
                <TableHead className="text-right">Difference</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingShifts ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
              ) : shiftsError ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-destructive" data-testid="status-shift-history-error">{(shiftsError as Error).message}</TableCell></TableRow>
              ) : !shifts?.length ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-12 text-center text-muted-foreground text-sm">No shifts recorded yet. Open a shift to begin.</TableCell>
                </TableRow>
              ) : shifts.map(s => {
                const diff = s.difference;
                return (
                  <TableRow key={s.id}>
                    <TableCell className="text-sm">{format(new Date(s.openedAt), "MMM d, yyyy")}</TableCell>
                    <TableCell className="font-medium text-sm">{s.staffName ?? "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{getDuration(s.openedAt, s.closedAt)}</TableCell>
                    <TableCell className="text-right text-sm">{formatCurrency(s.openingFloat)}</TableCell>
                    <TableCell className="text-right text-sm">
                      <span>{formatCurrency(s.totalSalesAmount)}</span>
                      <span className="text-muted-foreground text-xs ml-1">({s.totalSalesCount})</span>
                    </TableCell>
                    <TableCell className="text-right text-sm">{s.closingCash !== null ? formatCurrency(s.closingCash) : "—"}</TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {diff !== null ? (
                        <span className={diff >= 0 ? "text-emerald-600" : "text-destructive"}>
                          {diff >= 0 ? "+" : ""}{formatCurrency(diff)}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={s.status === "open" ? "default" : "secondary"} className="text-xs">
                        {s.status === "open" ? "Open" : "Closed"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Open Shift Dialog */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PlayCircle className="w-5 h-5 text-emerald-600" /> Open Register Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <label className="text-sm font-medium">Opening Float (₦)</label>
              <p className="text-xs text-muted-foreground mb-1.5">Count the cash in the register before you start selling</p>
              <Input
                type="number" min="0" step="50"
                placeholder="0.00"
                value={openingFloat}
                onChange={e => setOpeningFloat(e.target.value)}
                className="text-lg font-semibold"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notes <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Textarea
                placeholder="Any notes for this shift…"
                value={openNotes}
                onChange={e => setOpenNotes(e.target.value)}
                className="mt-1.5 resize-none"
                rows={2}
              />
            </div>
            {openMutation.error && (
              <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-md p-3">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {(openMutation.error as Error).message}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowOpenDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={() => openMutation.mutate()} disabled={openMutation.isPending} className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700">
                {openMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                Open Shift
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Shift Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><StopCircle className="w-5 h-5 text-destructive" /> Close Shift</DialogTitle>
          </DialogHeader>
          {currentShift && (
            <div className="space-y-4 pt-1">
              {refreshingCurrent && <p className="text-xs text-muted-foreground" data-testid="status-shift-refreshing">Refreshing cash totals…</p>}
              {currentError && <p className="text-sm text-destructive" data-testid="status-shift-close-error">{(currentError as Error).message}</p>}
              <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Opening float</span><span className="font-medium">{formatCurrency(currentShift.openingFloat)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cash sales this shift</span><span className="font-medium">{formatCurrency(currentShift.totalSalesAmount)}</span></div>
                <div className="flex justify-between border-t pt-2 font-semibold"><span>Expected in drawer</span><span>{formatCurrency(currentShift.openingFloat + currentShift.totalSalesAmount)}</span></div>
              </div>
              <div>
                <label className="text-sm font-medium">Actual Cash in Drawer (₦)</label>
                <p className="text-xs text-muted-foreground mb-1.5">Count all cash and enter the total</p>
                <Input
                  type="number" min="0" step="50"
                  placeholder="0.00"
                  value={closingCash}
                  onChange={e => setClosingCash(e.target.value)}
                  className="text-lg font-semibold"
                />
                {closingCash && (() => {
                  const actual = Number(closingCash);
                  const expected = currentShift.openingFloat + currentShift.totalSalesAmount;
                  const diff = actual - expected;
                  return (
                    <p className={`text-sm mt-1.5 font-medium flex items-center gap-1.5 ${diff >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                      {diff >= 0 ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                      {diff >= 0 ? `+${formatCurrency(diff)} over` : `${formatCurrency(Math.abs(diff))} short`}
                    </p>
                  );
                })()}
              </div>
              <div>
                <label className="text-sm font-medium">Notes <span className="text-muted-foreground font-normal">(optional)</span></label>
                <Textarea
                  placeholder="Handover notes, discrepancies…"
                  value={closeNotes}
                  onChange={e => setCloseNotes(e.target.value)}
                  className="mt-1.5 resize-none"
                  rows={2}
                />
              </div>
              {closeMutation.error && (
                <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-md p-3">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {(closeMutation.error as Error).message}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setShowCloseDialog(false)} className="flex-1">Cancel</Button>
                <Button data-testid="button-confirm-close-shift" onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending || refreshingCurrent || !!currentError || !closingCash} variant="destructive" className="flex-1 gap-2">
                  {closeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <StopCircle className="w-4 h-4" />}
                  Close Shift
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground flex items-center gap-1">{icon}{label}</span>
      <span className="font-semibold text-sm">{value}</span>
    </div>
  );
}

function getDuration(openedAt: string, closedAt: string | null): string {
  const start = new Date(openedAt).getTime();
  const end = closedAt ? new Date(closedAt).getTime() : Date.now();
  const mins = Math.floor((end - start) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
